/**
 * Resumen legible del periodo de prueba de un tenant.
 *
 * Regla del servidor (`listSubscriptionsOverview`): si el tenant NO tiene
 * suscripción, el fin de la prueba es `fecha de alta + trialDays`. Es el
 * TOTAL de días desde el alta, no días que se sumen a los ya consumidos.
 * Por eso subir "Días de trial" de 20 a 25 sube lo que queda en 5, no en 25.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

export interface TrialSummary {
  /** Días de prueba totales contados desde el alta. */
  totalDays: number;
  /** Días naturales ya consumidos desde el alta (nunca negativo). */
  elapsedDays: number;
  /** Días que quedan (negativo = ya expiró). Mismo criterio que la tabla de Suscripciones. */
  remainingDays: number;
  /** Fecha de fin calculada. */
  endsAt: Date;
}

/** `null` si el tenant no tiene días de trial configurados (null o 0). */
export function computeTrialSummary(
  trialDays: number | null | undefined,
  createdAt: string | Date,
  now: Date = new Date(),
): TrialSummary | null {
  if (!trialDays || trialDays <= 0) return null;
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return null;

  const endsAt = new Date(created.getTime() + trialDays * DAY_MS);
  return {
    totalDays: trialDays,
    elapsedDays: Math.max(0, Math.floor((now.getTime() - created.getTime()) / DAY_MS)),
    remainingDays: Math.ceil((endsAt.getTime() - now.getTime()) / DAY_MS),
    endsAt,
  };
}

/** Frase corta: "Total desde el alta: 25 días · han pasado 17 · quedan 8". */
export function describeTrialSummary(summary: TrialSummary): string {
  const plural = (n: number): string => `${n} día${Math.abs(n) === 1 ? "" : "s"}`;
  const left =
    summary.remainingDays > 0
      ? `quedan ${summary.remainingDays}`
      : summary.remainingDays === 0
        ? "expira hoy"
        : `expiró hace ${plural(-summary.remainingDays)}`;
  return `Total desde el alta: ${plural(summary.totalDays)} · han pasado ${summary.elapsedDays} · ${left}`;
}
